import { Button } from "@/components/Button";
import Modal from "@/components/Modal/Modal";
import { Select } from "@/components/Select";
import { H3, P } from "@/components/Typography";
import {
  useGetAllKlaviyoLists,
  useGetDatashipsKlaviyoLists,
  usePostUpdateKlaviyoList,
} from "@/requests/hooks/collect";
import { IFeatureFlagKeys } from "@/utils/featureFlags";
import formatError from "@/utils/formatError";
import { klaviyoListTypeToHumanReadable } from "@/utils/klaviyoListStringMapping";
import { useFlags } from "flagsmith/react";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { tv } from "tailwind-variants";

interface IProps {
  list?: IKlaviyoList;
  isOpen: boolean;
  closeModal: () => void;
}

const styles = tv({
  slots: {
    headingWrapper: "mb-20",
    titleWrapper: "mb-10",
    dropdownWrapper: "mb-30",
    ctaWrapper: "flex flex-row justify-center",
  },
});

const UpdateKlaviyoListModal = ({ list, isOpen, closeModal }: IProps) => {
  const { t } = useTranslation();
  const [updatedListId, setUpdatedListId] = useState<string | undefined>();
  const flags = useFlags<IFeatureFlagKeys>([
    "klaviyo-list-v2-endpoint-enabled",
  ]);
  const isKlaviyoListV2Enabled =
    flags?.["klaviyo-list-v2-endpoint-enabled"]?.enabled;
  const {
    mutate: updateKlaviyoList,
    isPending: isUpdateKlaviyoListPending,
    isSuccess: isUpdateKlaviyoListSuccess,
    isError: isUpdateKlaviyoListError,
    error: errorUpdateKlaviyoList,
  } = usePostUpdateKlaviyoList(isKlaviyoListV2Enabled);
  const {
    data: allKlaviyoLists,
    isLoading: isAllKlaviyoListsPending,
    isError: isAllKlaviyoListsError,
  } = useGetAllKlaviyoLists();
  const {
    refetch: refetchDatashipsKlaviyoLists,
    isRefetching: isDatashipsKlaviyoListsRefetching,
  } = useGetDatashipsKlaviyoLists(isKlaviyoListV2Enabled);
  const formattedListType =
    list?.type === "DOUBLE_OPT_IN"
      ? t("settings.integration.klaviyoLists.modal.doubleOptIn")
      : t("settings.integration.klaviyoLists.modal.singleOptIn");
  const { headingWrapper, titleWrapper, dropdownWrapper, ctaWrapper } =
    styles();

  useEffect(() => {
    const updatedKlaviyoListSuccess = async () => {
      await refetchDatashipsKlaviyoLists();
      toast.success(
        t("settings.integration.klaviyoLists.modal.success", {
          listType: formattedListType,
        }),
      );
      closeModal();
    };

    if (isUpdateKlaviyoListSuccess) {
      updatedKlaviyoListSuccess();
    }
  }, [isUpdateKlaviyoListSuccess]);

  const getHelperText = () => {
    if (!errorUpdateKlaviyoList) return;

    const { codes } = formatError(errorUpdateKlaviyoList);

    if (codes?.includes("INVALID_LIST_TYPE")) {
      return t("settings.integration.klaviyoLists.modal.error", {
        listType: formattedListType,
      });
    } else return t("generic.error");
  };

  const getLists = () =>
    allKlaviyoLists?.map(item => ({
      label: `${item.name} (${item.id})`,
      value: item,
    }));

  const updateSelectedList = (item: unknown) => {
    const { value } = item as { value: IKlaviyoList };
    setUpdatedListId(value.id);
  };

  const updateList = () => {
    if (!!list && !!updatedListId)
      updateKlaviyoList({
        type: list.type,
        id: updatedListId,
        channel: list.channel,
      });
  };

  const renderHeading = () => (
    <div className={headingWrapper()}>
      <div className={titleWrapper()}>
        <H3 className="font-bold">
          {t("settings.integration.klaviyoLists.modal.title", {
            listType: klaviyoListTypeToHumanReadable(list?.type),
          })}
        </H3>
      </div>
      <div className={titleWrapper()}>
        <Trans i18nKey="settings.integration.klaviyoLists.modal.description">
          <P>
            zero
            <a href="https://www.klaviyo.com/lists" target="_blank">
              one
            </a>
          </P>
        </Trans>
      </div>
      <P>
        {t("settings.integration.klaviyoLists.modal.descriptionWarning", {
          listType: klaviyoListTypeToHumanReadable(list?.type),
        })}
      </P>
    </div>
  );

  const renderDropdown = () => (
    <div className={dropdownWrapper()}>
      <Select
        label={t(
          "settings.integration.klaviyoLists.modal.dropdown.placeholder",
        )}
        name="updateKlaviyoLists"
        defaultValue={list}
        options={getLists()}
        onChange={updateSelectedList}
        isLoading={isAllKlaviyoListsPending}
        isDisabled={isAllKlaviyoListsError}
        helperText={getHelperText()}
        variant={
          isAllKlaviyoListsError || isUpdateKlaviyoListError
            ? "error"
            : undefined
        }
        closeMenuOnSelect
      />
    </div>
  );

  const renderCta = () => (
    <div className={ctaWrapper()}>
      <Button
        variant="primary"
        onClick={updateList}
        disabled={
          !updatedListId ||
          isUpdateKlaviyoListPending ||
          isAllKlaviyoListsPending ||
          isDatashipsKlaviyoListsRefetching
        }
        loading={
          isUpdateKlaviyoListPending || isDatashipsKlaviyoListsRefetching
        }
        trackingId={{
          eventName:
            "control-panel.email.integration-settings.klaviyo-lists.update",
          additionalProperties: {
            list_type: list?.type,
          },
        }}>
        {t("settings.integration.klaviyoLists.modal.cta")}
      </Button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} toggleIsOpen={closeModal} width="md">
      {renderHeading()}
      {renderDropdown()}
      {renderCta()}
    </Modal>
  );
};

export default UpdateKlaviyoListModal;
